import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  Tabs,
  Tab,
  MenuItem,
  Grid,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
} from '@mui/icons-material';
import api from '../config/api';
import { TERMS_VERSION } from '../config/terms';
import PartnerTermsModal from '../components/PartnerTermsModal';
import SEOHead from '../components/SEOHead';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import {
  DOCUMENT,
  documentPageCanvasSx,
  documentSubTabsSx,
  documentAuthPaperSx,
  documentAuthHeadingSx,
  documentAuthFieldSx,
  documentAuthFieldBaseSx,
  documentAuthButtonSx,
  documentAuthLinkSx,
  documentAuthAlertSx,
} from '../constants/documentTheme';
import AuthPageBrandMark from '../components/AuthPageBrandMark';
import useAuthPageBranding from '../hooks/useAuthPageBranding';
import { rememberAuthNext, sanitizeNextPath } from '../utils/authNext';
import { isMemoriesHost } from '../utils/memoriesHost';
import { persistClientSessionBranding } from '../contexts/ClientBrandingContext';
import { withClientInviteQuery } from '../utils/clientInvite';
import { hasPartnerSession, PARTNER_APP_HOME } from '../utils/partnerWorkspace';
//import SocialLoginButtons from '../components/SocialLoginButtons';
const BUSINESS_TYPES = [
  { value: 'travel_agency', label: 'Travel Agency' },
  { value: 'tour_operator', label: 'Tour Operator' },
  { value: 'hotel', label: 'Hotel / Accommodation' },
  { value: 'restaurant', label: 'Restaurant / Food Service' },
  { value: 'transport', label: 'Transportation Service' },
  { value: 'activity_provider', label: 'Activity Provider' },
  { value: 'other', label: 'Other' }
];

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const onMemoriesHost = isMemoriesHost();
  
  
  // Honeypot field (hidden, should remain empty)
  const [honeypot, setHoneypot] = useState('');
  
  // Determine initial tab based on URL
  const isPartnerPath = location.pathname.includes('/partner');
  const [activeTab, setActiveTab] = useState(onMemoriesHost ? 0 : (isPartnerPath ? 1 : 0));
  
  // User form state
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [emailLocked, setEmailLocked] = useState(false);
  const [inviteId, setInviteId] = useState(null);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const {
    brandToken,
    branding: clientInviteBranding,
    brandName,
    brandLogoUrl,
    buttonSx,
  } = useAuthPageBranding();

  // Partner form state
  const [partnerFormData, setPartnerFormData] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    companyAddress: '',
    businessRegistrationNumber: '',
    phoneNumber: '',
    businessType: '',
    businessTypeOther: '',
    website: ''
  });
  const [partnerSuccess, setPartnerSuccess] = useState(false);
  const [showPartnerTerms, setShowPartnerTerms] = useState(false);
  const [partnerTermsAccepted, setPartnerTermsAccepted] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const invite = searchParams.get('invite');
    const email = searchParams.get('email');
    const name = searchParams.get('name');
    const locked = searchParams.get('locked');

    if (email || name) {
      setActiveTab(0);
      const suggestedUsername = String(name || '')
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '')
        .slice(0, 20);
      setFormData(prev => ({
        ...prev,
        email: email || prev.email,
        name: name || prev.name,
        username: prev.username || (suggestedUsername.length >= 3 ? suggestedUsername : prev.username),
      }));
      setEmailLocked(locked === '1' || Boolean(name && email));
    }
    
    if (invite) {
      setInviteId(invite);
    }
    
    if (invite) {
      const fetchInvitation = async () => {
        try {
          const response = await api.get(`/invitations/${invite}`);
          setInvitation(response.data);
          if (!email) {
            setFormData(prev => ({
              ...prev,
              email: response.data.email || ''
            }));
          }
        } catch (err) {
          console.error('Error fetching invitation:', err);
          setError('Invalid or expired invitation');
        }
      };
      fetchInvitation();
    }
  }, [location]);

  // Update URL when tab changes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    if (newValue === 0) {
      navigate('/register', { replace: true });
    } else {
      navigate('/partner/signup', { replace: true });
    }
  };

  const validateForm = () => {
    const errors = {};
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.username) {
      errors.username = 'Username is required';
    } else if (!usernameRegex.test(formData.username)) {
      errors.username = 'Username must be 3-20 characters long and can only contain letters, numbers, and underscores';
    }

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePartnerChange = (e) => {
    const { name, value } = e.target;
    setPartnerFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Check honeypot - if filled, silently fail (bot detected)
    if (honeypot) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        navigate('/trips'); // Fake success to fool bot
      }, 2000);
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const next = sanitizeNextPath(new URLSearchParams(location.search).get('next'));
      const response = await api.post('/auth/register', {
        username: formData.username,
        email: formData.email,
        firstName: formData.name || undefined,
        password: formData.password,
        ...(next ? { next } : {}),
        ...(brandToken ? { brand: brandToken } : {}),
      });

      persistClientSessionBranding(response.data.branding);

      if (response.data.requiresVerification) {
        rememberAuthNext(next);
        navigate(withClientInviteQuery('/verify-email', brandToken, next ? { next } : {}), {
          state: { email: formData.email, next },
          replace: true,
        });
      } else if (response.data.token) {
        // Fallback: direct login (shouldn't happen with verification enabled)
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (inviteId) {
          setShowAcceptDialog(true);
        } else if (next) {
          if (next.includes('?')) {
            window.location.assign(next);
          } else {
            navigate(next, { replace: true });
          }
        } else {
          const from = location.state?.from;
          if (from?.pathname) {
            navigate({ pathname: from.pathname, search: from.search || '' }, { replace: true });
          } else {
            navigate('/trips', { replace: true });
          }
        }
      }
    } catch (err) {
      // Show detailed validation errors if available
      let errorMessage;
      if (err.response?.data?.details?.length > 0) {
        errorMessage = err.response.data.details.join('. ');
      } else {
        errorMessage = err.response?.data?.error || 'Failed to register';
      }
      setError(errorMessage);
      if (errorMessage.includes('already exists')) {
        setFormErrors(prev => ({
          ...prev,
          [errorMessage.includes('Username') ? 'username' : 'email']: errorMessage
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const validatePartnerForm = () => {
    if (!partnerFormData.companyName.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!partnerFormData.contactName.trim()) {
      setError('Contact name is required');
      return false;
    }
    if (!partnerFormData.contactEmail.trim()) {
      setError('Contact email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partnerFormData.contactEmail)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!partnerFormData.companyAddress.trim()) {
      setError('Company address is required');
      return false;
    }
    if (!partnerFormData.businessRegistrationNumber.trim()) {
      setError('Business registration number is required');
      return false;
    }
    if (!partnerFormData.phoneNumber.trim()) {
      setError('Phone number is required');
      return false;
    }
    if (!partnerFormData.businessType) {
      setError('Business type is required');
      return false;
    }
    if (partnerFormData.businessType === 'other' && !partnerFormData.businessTypeOther.trim()) {
      setError('Please specify your business type');
      return false;
    }
    return true;
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot - if filled, silently fail (bot detected)
    if (honeypot) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setPartnerSuccess(true); // Fake success to fool bot
      }, 2000);
      return;
    }
    
    if (!validatePartnerForm()) {
      return;
    }

    // If terms not accepted, show terms modal first
    if (!partnerTermsAccepted) {
      setShowPartnerTerms(true);
      return;
    }

    await submitPartnerApplication();
  };

  const submitPartnerApplication = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/partners/apply', {
        ...partnerFormData,
        termsAcceptedAt: new Date().toISOString(),
        termsVersion: TERMS_VERSION
      });
      
      if (response.data.success) {
        setPartnerSuccess(true);
        setPartnerFormData({
          companyName: '',
          contactName: '',
          contactEmail: '',
          companyAddress: '',
          businessRegistrationNumber: '',
          phoneNumber: '',
          businessType: '',
          businessTypeOther: '',
          website: ''
        });
        setPartnerTermsAccepted(false);
      }
    } catch (err) {
      console.error('Error submitting application:', err);
      // Show detailed validation errors if available
      if (err.response?.data?.details?.length > 0) {
        setError(err.response.data.details.join('. '));
      } else {
        setError(err.response?.data?.error || 'Failed to submit application. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerTermsAccept = () => {
    setPartnerTermsAccepted(true);
    setShowPartnerTerms(false);
    // Auto-submit after accepting terms
    submitPartnerApplication();
  };

  const handleAcceptInvite = async () => {
    if (!inviteId) return;
    try {
      const res = await api.get(`/invitations/${inviteId}`);
      const tripId = res.data.trip_id;
      await api.post(`/trips/${tripId}/accept-invitation`);
      setShowAcceptDialog(false);
      navigate(`/trips/${tripId}`);
    } catch (err) {
      setError('Failed to accept invitation.');
      setShowAcceptDialog(false);
      navigate('/trips');
    }
  };

  if (hasPartnerSession()) {
    return <Navigate to={PARTNER_APP_HOME} replace />;
  }


  // Partner success state
  if (partnerSuccess) {
    return (
      <Box sx={documentPageCanvasSx}>
        <DocumentMarketingHero
          eyebrow={t('home.footer.partners')}
          title="Application Submitted"
          subtitle="We'll review your partner application and follow up by email"
        />
        <Container maxWidth="sm" sx={{ py: { xs: 3, md: 4 } }}>
          <Paper elevation={0} sx={{ ...documentAuthPaperSx, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7, color: DOCUMENT.ink2, fontFamily: DOCUMENT.sans }}>
              Thank you for your interest in becoming a Déjà Vu Nomad partner! 
              We've received your application and our team will review it shortly.
            </Typography>
            <Typography variant="body2" sx={{ mb: 4, color: DOCUMENT.ink3, fontFamily: DOCUMENT.sans }}>
              You will receive an email notification once your application has been reviewed.
              This typically takes 1-3 business days.
            </Typography>
            <Button
              variant="contained"
              disableElevation
              onClick={() => navigate('/')}
              sx={{ ...documentAuthButtonSx, width: 'auto', px: 4 }}
            >
              Return to Home
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title={brandName ? `Sign Up - ${brandName}` : 'Sign Up - Déjà Vu Nomad'}
        description={
          brandName
            ? `Create your ${brandName} account.`
            : 'Create your Déjà Vu Nomad account to start planning trips, join group vacations, and discover travel deals.'
        }
        canonical="https://dejavunomad.com/register"
        noIndex={true}
      />
      <DocumentMarketingHero
        eyebrow={activeTab === 0 ? t('auth.getStarted') : t('home.footer.partners')}
        title={
          activeTab === 0
            ? (onMemoriesHost
              ? t('memories.joinTitle')
              : (brandName ? t('auth.joinBrandTitle', { name: brandName }) : t('auth.joinTitle')))
            : 'Become a Partner'
        }
        subtitle={
          activeTab === 0
            ? (onMemoriesHost
              ? t('memories.joinSubtitle')
              : (brandName ? t('auth.joinBrandSubtitle', { name: brandName }) : t('auth.joinSubtitle')))
            : 'Join our partner network and grow your business'
        }
      />

      {/* Form Section */}
      <Container maxWidth={activeTab === 0 ? "sm" : "md"} sx={{ py: { xs: 3, md: 4 } }}>
        <Paper elevation={0} sx={documentAuthPaperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <AuthPageBrandMark
              brandName={brandName}
              brandLogoUrl={brandLogoUrl}
              memories={onMemoriesHost}
            />
            <Typography variant="h5" sx={documentAuthHeadingSx}>
              {activeTab === 0 ? t('auth.createAccountHeading') : 'Partner Application'}
            </Typography>
          </Box>

          {!onMemoriesHost && !brandName && (
          <Box sx={{ mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              centered
              sx={documentSubTabsSx}
            >
              <Tab label="Traveler" />
              <Tab label="Partner" />
            </Tabs>
          </Box>
          )}

          {error && (
            <Alert severity="error" sx={documentAuthAlertSx}>
              {error}
            </Alert>
          )}

          {activeTab === 0 ? (
            /* User Registration Form */
            <>
              {brandName && (
                <Alert severity="info" sx={documentAuthAlertSx}>
                  {t('auth.joiningBrand', { name: clientInviteBranding?.name || brandName })}
                </Alert>
              )}

              {invitation && (
                <Alert severity="info" sx={documentAuthAlertSx}>
                  {t('auth.inviteComplete')}
                </Alert>
              )}

              {showAcceptDialog && (
                <Alert
                  severity="info"
                  sx={documentAuthAlertSx}
                  action={
                    <Button
                      variant="contained"
                      size="small"
                      disableElevation
                      onClick={handleAcceptInvite}
                      sx={{ ...documentAuthButtonSx, mt: 0, mb: 0, width: 'auto', minHeight: 36 }}
                    >
                      {t('auth.acceptInvitation')}
                    </Button>
                  }
                >
                  {t('auth.inviteAccept')}
                </Alert>
              )}

              {/* TODO: Re-enable Google Sign-In once mobile app auth is resolved */}
              {/* <SocialLoginButtons mode="register" /> */}

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  id="name"
                  label={t('auth.name')}
                  name="name"
                  autoComplete="name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  sx={{ ...documentAuthFieldSx, mt: 0 }}
                  inputProps={{ 'data-testid': 'register-name' }}
                />
                <TextField
                  fullWidth
                  label={t('auth.username')}
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  error={!!formErrors.username}
                  helperText={formErrors.username}
                  sx={{ ...documentAuthFieldSx, mt: 0 }}
                />
                <TextField
                  fullWidth
                  label={t('auth.emailAddress')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={emailLocked}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  sx={documentAuthFieldSx}
                />
                <TextField
                  fullWidth
                  label={t('auth.password')}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  sx={documentAuthFieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label={t('auth.confirmPassword')}
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                  sx={documentAuthFieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          disabled={loading}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Honeypot field - hidden from users, catches bots */}
                <TextField
                  name="website_url"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  autoComplete="off"
                  tabIndex={-1}
                  sx={{
                    position: 'absolute',
                    left: '-9999px',
                    opacity: 0,
                    height: 0,
                    width: 0,
                  }}
                  aria-hidden="true"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  disableElevation
                  sx={buttonSx}
                >
                  {loading ? (
                    <CircularProgress size={22} sx={{ color: 'inherit' }} />
                  ) : (
                    onMemoriesHost
                      ? t('memories.createAccountButton')
                      : (brandName
                        ? t('auth.createBrandAccountButton', { name: brandName })
                        : t('auth.createAccountButton'))
                  )}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Link 
                    component={RouterLink} 
                    to={withClientInviteQuery(
                      '/login',
                      brandToken,
                      (() => {
                        const next = sanitizeNextPath(new URLSearchParams(location.search).get('next'));
                        return next ? { next } : {};
                      })()
                    )} 
                    variant="body1"
                    sx={documentAuthLinkSx}
                  >
                    {t('auth.alreadyHaveAccount')} {t('auth.signInLink')}
                  </Link>
                </Box>
              </Box>
            </>
          ) : (
            /* Partner Registration Form */
            <Box component="form" onSubmit={handlePartnerSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="companyName"
                    value={partnerFormData.companyName}
                    onChange={handlePartnerChange}
                    required
                    disabled={loading}
                    sx={documentAuthFieldBaseSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    name="contactName"
                    value={partnerFormData.contactName}
                    onChange={handlePartnerChange}
                    required
                    disabled={loading}
                    sx={documentAuthFieldBaseSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    name="contactEmail"
                    type="email"
                    value={partnerFormData.contactEmail}
                    onChange={handlePartnerChange}
                    required
                    disabled={loading}
                    sx={documentAuthFieldBaseSx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Address"
                    name="companyAddress"
                    value={partnerFormData.companyAddress}
                    onChange={handlePartnerChange}
                    required
                    disabled={loading}
                    multiline
                    rows={2}
                    sx={documentAuthFieldBaseSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Business Registration Number"
                    name="businessRegistrationNumber"
                    value={partnerFormData.businessRegistrationNumber}
                    onChange={handlePartnerChange}
                    required
                    disabled={loading}
                    sx={documentAuthFieldBaseSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phoneNumber"
                    type="tel"
                    value={partnerFormData.phoneNumber}
                    onChange={handlePartnerChange}
                    required
                    disabled={loading}
                    sx={documentAuthFieldBaseSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Website URL (Optional)"
                    name="website"
                    type="url"
                    value={partnerFormData.website}
                    onChange={handlePartnerChange}
                    disabled={loading}
                    placeholder="https://www.example.com"
                    sx={documentAuthFieldBaseSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Type of Business"
                    name="businessType"
                    value={partnerFormData.businessType}
                    onChange={handlePartnerChange}
                    required
                    disabled={loading}
                    sx={documentAuthFieldBaseSx}
                  >
                    {BUSINESS_TYPES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {partnerFormData.businessType === 'other' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Please Specify Business Type"
                      name="businessTypeOther"
                      value={partnerFormData.businessTypeOther}
                      onChange={handlePartnerChange}
                      required
                      disabled={loading}
                      sx={documentAuthFieldBaseSx}
                    />
                  </Grid>
                )}

                {/* Honeypot field - hidden from users, catches bots */}
                <TextField
                  name="company_website_url"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  autoComplete="off"
                  tabIndex={-1}
                  sx={{
                    position: 'absolute',
                    left: '-9999px',
                    opacity: 0,
                    height: 0,
                    width: 0,
                  }}
                  aria-hidden="true"
                />
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                disableElevation
                sx={documentAuthButtonSx}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: 'inherit' }} />
                ) : (
                  'Submit Application'
                )}
              </Button>

              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mb: 2, color: DOCUMENT.ink3, fontFamily: DOCUMENT.sans }}>
                By clicking submit, you'll be asked to review and accept our Partner Agreement including commission terms. We'll review your application within 1-3 business days.
              </Typography>

              <Box sx={{ textAlign: 'center' }}>
                <Link 
                  component={RouterLink} 
                  to="/partner/login" 
                  variant="body1"
                  sx={documentAuthLinkSx}
                >
                  Already a partner? Sign in to your account
                </Link>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Partner Terms Modal */}
      <PartnerTermsModal
        open={showPartnerTerms}
        onClose={() => setShowPartnerTerms(false)}
        onAccept={handlePartnerTermsAccept}
        companyName={partnerFormData.companyName}
      />
    </Box>
  );
};

export default Register;
